import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { Box, CardActionArea, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { formatBytesToHumanReadable } from "@src/appV2/lib/files/utils";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { useRef } from "react";

interface SingleBoxFileUploaderProps {
  selectedFile: File | undefined;
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

export function SingleBoxFileUploader(props: SingleBoxFileUploaderProps) {
  const { selectedFile, onFileSelected, onFileRemoved } = props;
  const { showErrorToast } = useToast();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = (file: File) => {
    onFileSelected(file);
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const handleFileRemoved = () => {
    onFileRemoved();
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      handleFileRemoved();
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast("File is too large. Please upload a smaller file.");
      return;
    }

    handleFileSelected(file);
  };

  return (
    <Box>
      {selectedFile ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.primary}`,
            borderRadius: 4,
            paddingLeft: 5,
            paddingRight: 2,
            paddingY: 4,
          })}
        >
          <Stack direction="row" spacing={4} alignItems="center">
            <CbhIcon type="document" />
            <Stack spacing={1}>
              <Text>{selectedFile.name}</Text>
              <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                {formatBytesToHumanReadable(selectedFile.size)}
              </Text>
            </Stack>
          </Stack>
          <IconButton
            variant="ghost"
            iconType="close"
            sx={{ color: (theme) => theme.palette.text.tertiary }}
            onClick={handleFileRemoved}
          />
        </Stack>
      ) : (
        <CardActionArea onClick={() => inputFileRef.current?.click()}>
          <Stack
            spacing={3}
            sx={(theme) => ({
              border: `2px dashed ${theme.palette.border?.primary}`,
              borderRadius: 4,
              paddingY: 10,
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.background.quaternary,
              },
            })}
          >
            <CbhIcon
              type="share"
              size="large"
              sx={{ color: (theme) => theme.palette.text.tertiary }}
            />
            <Text
              variant="body2"
              align="center"
              sx={{ color: (theme) => theme.palette.text.tertiary }}
            >
              Tap to upload a photo or document
            </Text>
          </Stack>
        </CardActionArea>
      )}

      <input
        ref={inputFileRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
